import WolfTextLink from '@components/ui/WolfTextLink';
import { graphQlClient } from '@config/graphqlClient';
import ProcessExecutionGraphQL from '@graphql/processExecution.queries';
import { useTranslation } from '@hooks/useTranslation';
import { SnackType } from '@models/common.model';
import { getLatestExecutionHistoryRequest, ProcessExecution, StepExecution, StepExecutionStatus } from '@models/processExecution.model';
import { ConfirmationType } from '@models/step.model';
import { UserRoles } from '@models/user.model';
import {
  Cancel,
  CheckCircle,
  CheckCircleOutline,
  Error,
  ErrorOutline,
  KeyboardArrowDown,
  KeyboardArrowUp,
  PendingOutlined,
  PlayCircleOutline,
  RedoOutlined
} from '@mui/icons-material';
import {
  Box,
  Chip,
  Collapse,
  Icon,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography
} from '@mui/material';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import { appendActionMessage } from '@redux/reducers/actionMessages.reducer';
import { RootState } from '@redux/store';
import { DateTime } from 'luxon';
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

interface ProcessExecutionsTableProps {
  processId: string;
  modalMode?: boolean;
}

const ProcessExecutionsTable: React.FC<ProcessExecutionsTableProps> = ({ processId, modalMode = false }) => {
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { role } = useAppSelector((state: RootState) => state.user);
  const isUserRole = role === UserRoles.USER;
  const localeProcesses = useTranslation('processes');
  const localeCommon = useTranslation('common');

  const [executions, setExecutions] = useState<ProcessExecution[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedExecution, setExpandedExecution] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response: getLatestExecutionHistoryRequest = await graphQlClient.request(
          ProcessExecutionGraphQL.queries.getLatestExecutionHistory,
          { processId },
        );

        if (response.getLatestExecutionHistory) {
          // Sort by most recent first
          const sorted = response.getLatestExecutionHistory.sort(
            (a, b) => new Date(b?.startedAt || 0).getTime() - new Date(a?.startedAt || 0).getTime(),
          );
          setExecutions(sorted);
        }
      } catch (error) {
        console.error('Error fetching execution history:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [processId]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'COMPLETED':
        return 'success';
      case 'FAILED':
        return 'error';
      case 'CANCELLED':
        return 'default';
      case 'IN_PROGRESS':
        return 'info';
      case 'SKIPPED':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'COMPLETED':
        return localeProcesses['completed'];
      case 'FAILED':
        return localeProcesses['failed'];
      case 'CANCELLED':
        return localeProcesses['cancelled'];
      case 'IN_PROGRESS':
        return localeProcesses['in_progress'];
      case 'SKIPPED':
        return localeProcesses['skipped'];
      default:
        return status;
    }
  };

  const getStepStatusIcon = (status: StepExecutionStatus) => {
    switch (status) {
      case StepExecutionStatus.COMPLETED:
        return <CheckCircleOutline sx={{ color: 'success.main', fontSize: 16 }} />;
      case StepExecutionStatus.FAILED:
        return <ErrorOutline sx={{ color: 'error.main', fontSize: 16 }} />;
      case StepExecutionStatus.SKIPPED:
        return <RedoOutlined sx={{ color: 'warning.main', fontSize: 16 }} />;
      case StepExecutionStatus.IN_PROGRESS:
        return <PlayCircleOutline sx={{ color: 'info.main', fontSize: 16 }} />;
      default:
        return <PendingOutlined sx={{ color: 'text.secondary', fontSize: 16 }} />;
    }
  };

  const calculateDuration = (startedAt: string, completedAt?: string | null) => {
    const start = new Date(startedAt).getTime();
    const end = completedAt ? new Date(completedAt).getTime() : Date.now();
    const durationMs = end - start;

    const hours = Math.floor(durationMs / 3600000);
    const minutes = Math.floor((durationMs % 3600000) / 60000);

    if (hours > 0) return `${hours}h ${minutes}m`;
    if (minutes > 0) return `${minutes}m`;
    return '< 1m';
  };

  const isStuckExecution = (startedAt: string, completedAt?: string | null) => {
    const start = new Date(startedAt).getTime();
    const end = completedAt ? new Date(completedAt).getTime() : Date.now();
    const durationMs = end - start;
    const durationMinutes = durationMs / 60000;
    return durationMinutes > 5;
  };

  const handleAccordionChange = (executionId: string) => {
    setExpandedExecution(expandedExecution === executionId ? null : executionId);
  };

  const handleUpdateExecutionStatus = async (executionId: string, status: string) => {
    try {
      await graphQlClient.request(ProcessExecutionGraphQL.mutations.completeProcessExecution, {
        id: executionId,
        status,
      });

      // Refresh the executions list
      const response: getLatestExecutionHistoryRequest = await graphQlClient.request(
        ProcessExecutionGraphQL.queries.getLatestExecutionHistory,
        { processId },
      );

      if (response.getLatestExecutionHistory) {
        const sorted = response.getLatestExecutionHistory.sort(
          (a, b) => new Date(b?.startedAt || 0).getTime() - new Date(a?.startedAt || 0).getTime(),
        );
        setExecutions(sorted);
      }

      const statusMessage =
        status === 'COMPLETED'
          ? localeProcesses['executionCompleted']
          : status === 'FAILED'
            ? localeProcesses['executionFailed']
            : localeProcesses['executionCancelled'];

      dispatch(
        appendActionMessage({
          message: statusMessage,
          type: SnackType.SUCCESS,
        }),
      );
    } catch (error) {
      console.error('Error updating execution status:', error);
      dispatch(
        appendActionMessage({
          message: localeCommon['requestError'],
          type: SnackType.ERROR,
        }),
      );
    }
  };

  if (loading) {
    return (
      <Box className="p-4">
        <Typography variant="body2">{localeCommon['loading']}...</Typography>
      </Box>
    );
  }

  if (executions.length === 0) {
    return (
      <Box className="p-4">
        <Typography variant="body2" className="text-gray-600">
          {localeProcesses['noExecutionsYet']}
        </Typography>
      </Box>
    );
  }

  return (
    <Box className={`w-full h-full flex flex-col ${!modalMode ? 'mb-28' : ''}`}>
      <Box className="flex items-center justify-between mb-4 flex-shrink-0">
        <Typography variant="h4semibold">
          {localeProcesses['executionHistory']}
        </Typography>
        {executions.length === 5 && (
          <WolfTextLink onClick={() => history.push(`/executions?processId=${processId}`)}>
            {localeProcesses['viewAllExecutions']}
          </WolfTextLink>
        )}
      </Box>

      <TableContainer component={Paper} className="mb-4 flex-1" sx={{ maxHeight: modalMode ? 'none' : '400px', overflowY: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell>{localeCommon['status']}</TableCell>
              <TableCell>{localeProcesses['executedBy']}</TableCell>
              <TableCell>{localeCommon['date']}</TableCell>
              <TableCell>{localeProcesses['duration']}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {executions.map((execution) => (
              <React.Fragment key={execution.processExecutionId}>
                <TableRow hover className={execution?.isRedacted ? 'opacity-70' : ''}>
                  <TableCell>
                    {!execution?.isRedacted ? (
                      <IconButton
                        size="small"
                        onClick={() => handleAccordionChange(execution?.processExecutionId || '')}>
                        {expandedExecution === execution?.processExecutionId ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                      </IconButton>
                    ) : (
                      <IconButton size="small" disabled>
                        <KeyboardArrowDown className="opacity-30" />
                      </IconButton>
                    )}
                  </TableCell>
                  <TableCell>
                    <Box className="flex items-center gap-2">
                      <Chip label={getStatusLabel(execution?.status || '')} color={getStatusColor(execution?.status || '')} size="small" />
                      {!isUserRole &&
                        !execution?.isRedacted &&
                        execution?.status === 'IN_PROGRESS' &&
                        execution?.startedAt &&
                        isStuckExecution(execution.startedAt, execution?.completedAt) && (
                        <Box className="flex gap-1">
                          <Tooltip title={localeProcesses['markStuckExecutionAsCompletedTooltip']}>
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() =>
                                handleUpdateExecutionStatus(execution?.processExecutionId || '', 'COMPLETED')
                              }>
                              <CheckCircle fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={localeProcesses['markStuckExecutionAsFailedTooltip']}>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() =>
                                handleUpdateExecutionStatus(execution?.processExecutionId || '', 'FAILED')
                              }>
                              <Error fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={localeProcesses['markStuckExecutionAsCancelledTooltip']}>
                            <IconButton
                              size="small"
                              onClick={() =>
                                handleUpdateExecutionStatus(execution?.processExecutionId || '', 'CANCELLED')
                              }>
                              <Cancel fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    {execution?.isRedacted
                      ? <Typography variant="body2" className="text-gray-500 italic">{localeProcesses['executionByAnotherUser']}</Typography>
                      : (execution?.startedByUser?.displayName || execution?.startedByUser?.email || '-')}
                  </TableCell>
                  <TableCell>
                    {execution?.startedAt ? DateTime.fromISO(execution.startedAt).toFormat('dd-MM-yyyy HH:mm') : '-'}
                  </TableCell>
                  <TableCell>
                    {execution?.startedAt ? calculateDuration(execution.startedAt, execution?.completedAt) : '-'}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={5}>
                    <Collapse in={!execution?.isRedacted && expandedExecution === execution?.processExecutionId} timeout="auto" unmountOnExit>
                      <Box className="py-4">
                        {execution?.stepExecutions && execution?.stepExecutions?.length > 0 ? (
                          <Box className="flex flex-col gap-2">
                            <Typography variant="body2" className="font-semibold mb-2">
                              {localeCommon['allSteps']}:
                            </Typography>
                            {(() => {
                              const allSteps = execution?.stepExecutions || [];

                              // Helper to identify container steps by stepType (case-insensitive)
                              const isContainerType = (stepType?: string) => {
                                const type = stepType?.toUpperCase();
                                return type === 'SWITCH' || type === 'REPEAT';
                              };

                              // Get top-level steps (containers + regular steps) - substeps have substepOrder set (1-N)
                              const topLevelSteps = allSteps
                                .filter(s => s.substepOrder == null)
                                .sort((a, b) => (a.sequenceOrder ?? 0) - (b.sequenceOrder ?? 0));

                              // Get all substeps (have substepOrder set)
                              const allSubsteps = allSteps.filter(s => s.substepOrder != null);

                              // Helper to get child steps for a container
                              // First try childStepExecutions from API, then fall back to finding by parentStepExecutionId
                              const getChildSteps = (container: StepExecution) => {
                                if (container.childStepExecutions && container.childStepExecutions.length > 0) {
                                  return container.childStepExecutions;
                                }
                                // Find substeps that belong to this container by parentStepExecutionId
                                return allSubsteps
                                  .filter(s => s.parentStepExecutionId === container.stepExecutionId)
                                  .sort((a, b) => (a.substepOrder ?? 0) - (b.substepOrder ?? 0));
                              };

                              let stepCounter = 0;
                              return topLevelSteps.map((step) => {
                                stepCounter++;
                                const stepIndex = stepCounter;
                                const stepType = step.stepSnapshot?.stepType?.toUpperCase();

                                if (isContainerType(stepType)) {
                                  // Render container step (SWITCH/REPEAT) with nested substeps
                                  const isRepeat = stepType === 'REPEAT';
                                  const childSteps = getChildSteps(step);
                                  const containerName = step.stepSnapshot?.name ||
                                    (isRepeat ? localeProcesses['repeatStep'] : localeProcesses['switchStep']);

                                  if (isRepeat) {
                                    // For REPEAT: group substeps by iterationNumber
                                    const iterationSubsteps = childSteps.filter(s => s.iterationNumber != null);
                                    const iterations = Array.from(new Set(iterationSubsteps.map(s => s.iterationNumber!))).sort((a, b) => a - b);

                                    return (
                                      <Box key={step.stepExecutionId} className="flex flex-col gap-1">
                                        {/* Container header - no status icon since container has no status */}
                                        <Box className="flex items-center gap-2 p-3 bg-utilityYellowLight rounded-t border-l-4 border-utilityYellow">
                                          <Typography variant="body2" className="text-gray-500 font-semibold min-w-[24px]">
                                            {stepIndex}.
                                          </Typography>
                                          <Typography variant="body2" className="flex-1 font-medium">
                                            {containerName}
                                          </Typography>
                                          <Chip
                                            label={`${iterations.length} ${localeProcesses['iteration']}${iterations.length > 1 ? 's' : ''}`}
                                            color="default"
                                            size="small"
                                          />
                                        </Box>
                                        {/* Iterations */}
                                        {iterations.map((iterNum) => {
                                          const iterSubsteps = iterationSubsteps
                                            .filter(s => s.iterationNumber === iterNum)
                                            .sort((a, b) => (a.substepOrder ?? 0) - (b.substepOrder ?? 0));
                                          const completedCount = iterSubsteps.filter(s => s.status === StepExecutionStatus.COMPLETED).length;
                                          const failedCount = iterSubsteps.filter(s => s.status === StepExecutionStatus.FAILED).length;

                                          return (
                                            <Box key={`iter-${iterNum}`} className="ml-6 border-l-2 border-gray-200 pl-2">
                                              <Box className="flex items-center gap-2 py-1 px-2 bg-gray-50 rounded">
                                                {failedCount > 0 ? (
                                                  <ErrorOutline sx={{ color: 'error.main', fontSize: 14 }} />
                                                ) : completedCount === iterSubsteps.length ? (
                                                  <CheckCircleOutline sx={{ color: 'success.main', fontSize: 14 }} />
                                                ) : (
                                                  <PendingOutlined sx={{ color: 'text.secondary', fontSize: 14 }} />
                                                )}
                                                <Typography variant="caption" className="font-semibold">
                                                  {localeProcesses['iteration']} {iterNum}: {completedCount}/{iterSubsteps.length} {localeProcesses['completed']}
                                                </Typography>
                                              </Box>
                                              {/* Substeps in this iteration */}
                                              {iterSubsteps.map((substep, subIdx) => (
                                                <Box key={substep.stepExecutionId} className="ml-4 py-1 px-2 flex items-center gap-2">
                                                  <Typography variant="caption" className="text-gray-400 min-w-[32px]">
                                                    {stepIndex}.{subIdx + 1}
                                                  </Typography>
                                                  <Icon className="!flex items-center" sx={{ fontSize: 14 }}>
                                                    {getStepStatusIcon(substep.status || StepExecutionStatus.PENDING)}
                                                  </Icon>
                                                  <Typography variant="caption" className="flex-1">
                                                    {substep.stepSnapshot?.name || '-'}
                                                  </Typography>
                                                  {substep.notes && (
                                                    <Typography variant="caption" className="text-gray-500 truncate max-w-[150px]">
                                                      ({substep.notes})
                                                    </Typography>
                                                  )}
                                                </Box>
                                              ))}
                                            </Box>
                                          );
                                        })}
                                      </Box>
                                    );
                                  } else {
                                    // SWITCH: show all substeps without iteration grouping
                                    const sortedSubsteps = [...childSteps].sort((a, b) => (a.substepOrder ?? 0) - (b.substepOrder ?? 0));
                                    const completedCount = sortedSubsteps.filter(s => s.status === StepExecutionStatus.COMPLETED).length;
                                    const failedCount = sortedSubsteps.filter(s => s.status === StepExecutionStatus.FAILED).length;
                                    const skippedCount = sortedSubsteps.filter(s => s.status === StepExecutionStatus.SKIPPED).length;

                                    return (
                                      <Box key={step.stepExecutionId} className="flex flex-col gap-1">
                                        {/* Container header - no status icon since container has no status */}
                                        <Box className="flex items-center gap-2 p-3 bg-utilityGreenLight rounded-t border-l-4 border-utilityGreen">
                                          <Typography variant="body2" className="text-gray-500 font-semibold min-w-[24px]">
                                            {stepIndex}.
                                          </Typography>
                                          <Typography variant="body2" className="flex-1 font-medium">
                                            {containerName}
                                          </Typography>
                                          <Chip
                                            label={failedCount > 0
                                              ? `${completedCount}/${sortedSubsteps.length} ${localeProcesses['completed']}, ${failedCount} ${localeProcesses['failed']?.toLowerCase() || 'failed'}`
                                              : skippedCount > 0
                                                ? `${completedCount}/${sortedSubsteps.length} ${localeProcesses['completed']}, ${skippedCount} ${localeProcesses['skipped']?.toLowerCase() || 'skipped'}`
                                                : `${completedCount}/${sortedSubsteps.length} ${localeProcesses['completed']}`}
                                            color="default"
                                            size="small"
                                          />
                                        </Box>
                                        {/* Substeps */}
                                        <Box className="ml-6 border-l-2 border-gray-200 pl-2">
                                          {sortedSubsteps.map((substep, subIdx) => (
                                            <Box key={substep.stepExecutionId} className="py-1 px-2 flex items-center gap-2">
                                              <Typography variant="caption" className="text-gray-400 min-w-[32px]">
                                                {stepIndex}.{String.fromCharCode(65 + subIdx)}
                                              </Typography>
                                              <Icon className="!flex items-center" sx={{ fontSize: 14 }}>
                                                {getStepStatusIcon(substep.status || StepExecutionStatus.PENDING)}
                                              </Icon>
                                              <Typography variant="caption" className="flex-1">
                                                {substep.stepSnapshot?.name || '-'}
                                              </Typography>
                                              {substep.notes && (
                                                <Typography variant="caption" className="text-gray-500 truncate max-w-[150px]">
                                                  ({substep.notes})
                                                </Typography>
                                              )}
                                            </Box>
                                          ))}
                                        </Box>
                                      </Box>
                                    );
                                  }
                                } else {
                                  // Render regular step
                                  return (
                                    <Box
                                      key={step.stepExecutionId}
                                      className="flex flex-col gap-1 p-3 bg-primaryFrozen rounded">
                                      <Box className="flex items-center gap-2">
                                        <Typography variant="body2" className="text-gray-500 font-semibold min-w-[24px]">
                                          {stepIndex}.
                                        </Typography>
                                        <Box className="flex items-center">
                                          <Icon className="!flex items-center">{getStepStatusIcon(step.status || StepExecutionStatus.PENDING)}</Icon>
                                        </Box>
                                        <Typography variant="body2" className="flex-1 font-medium">
                                          {step.stepSnapshot?.name || '-'}
                                        </Typography>
                                        <Chip
                                          label={getStatusLabel(step.status || '')}
                                          color={getStatusColor(step.status || '')}
                                          size="small"
                                        />
                                      </Box>
                                      {step.errorDetails?.message && (
                                        <Box className="ml-8 mt-1">
                                          <Typography variant="caption" className="text-error">
                                            {localeProcesses['errorDetails']}: {step.errorDetails.message}
                                          </Typography>
                                        </Box>
                                      )}
                                      {step.stepSnapshot?.stepData?.condition && (
                                        <Box className="ml-8 mt-1">
                                          <Typography variant="caption" className="text-feedbackWarning font-semibold">
                                            {localeProcesses['condition']}: {step.stepSnapshot.stepData.condition}
                                          </Typography>
                                        </Box>
                                      )}
                                      {step.notes && (
                                        <Box className="ml-8 mt-1">
                                          <Typography variant="caption" className="text-gray-600">
                                            {localeProcesses['notes']}: {step.notes}
                                          </Typography>
                                        </Box>
                                      )}
                                      {step.confirmationResponse && step.stepSnapshot?.stepData?.confirmationType && (
                                        <Box className="ml-8 mt-1">
                                          <Typography variant="caption" className="text-primary font-semibold">
                                            {step.stepSnapshot.stepData.confirmationType === ConfirmationType.BOOLEAN
                                              ? `✓ ${localeProcesses['confirmed']}`
                                              : `${step.stepSnapshot.stepData.confirmationQuestion || localeProcesses['confirmationResponse']}: ${step.confirmationResponse}`}
                                          </Typography>
                                        </Box>
                                      )}
                                    </Box>
                                  );
                                }
                              });
                            })()}
                          </Box>
                        ) : (
                          <Typography variant="body2" className="text-gray-600">
                            {localeProcesses['noSteps']}
                          </Typography>
                        )}
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default ProcessExecutionsTable;
